import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useUiI18n } from "../../I18n";
import "./ErrorRegion.css";

interface ErrorRegionProps {
	children: ReactNode;
	name?: string;
}

interface ErrorRegionBoundaryProps extends ErrorRegionProps {
	labels: { title: string; description: string; retry: string; back: string };
}

interface ErrorRegionState {
	failed: boolean;
}

class ErrorRegionBoundary extends Component<ErrorRegionBoundaryProps, ErrorRegionState> {
	public state: ErrorRegionState = { failed: false };
	private reloadRequired = false;

	public static getDerivedStateFromError(): ErrorRegionState {
		return { failed: true };
	}

	public componentDidCatch(error: Error, info: ErrorInfo): void {
		this.reloadRequired =
			/Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk .* failed|ChunkLoadError/i.test(
				error.message,
			) || error.name === "ChunkLoadError";
		console.error(`Page region failed: ${this.props.name ?? "page"}`, error, info.componentStack);
	}

	private retry = (): void => {
		if (this.reloadRequired) {
			window.location.reload();
			return;
		}
		this.setState({ failed: false });
	};

	public render(): ReactNode {
		if (!this.state.failed) return this.props.children;
		const { labels } = this.props;
		return (
			<section className="page-error-region" role="alert" data-testid="page-error-region">
				<div className="page-error-region__sigil" aria-hidden="true">
					✦
				</div>
				<h2>{labels.title}</h2>
				<p>{labels.description}</p>
				<div className="page-error-region__actions">
					<button type="button" onClick={this.retry}>
						{labels.retry}
					</button>
					<Link to="/dashboard">{labels.back}</Link>
				</div>
			</section>
		);
	}
}

export const ErrorRegion = ({ children, name }: ErrorRegionProps) => {
	const { t } = useUiI18n();
	return (
		<ErrorRegionBoundary
			name={name}
			labels={{
				title: t("page.errorTitle"),
				description: t("page.errorDescription"),
				retry: t("page.retry"),
				back: t("common.backToDashboard"),
			}}
		>
			{children}
		</ErrorRegionBoundary>
	);
};
